/**
 * Invite Service - Manages user invitation operations
 * Handles sending, accepting, revoking, and resending invites
 */

import { db, functions } from '@/js/firebase'
import { httpsCallable } from 'firebase/functions'
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore'
import { logger } from './loggerService'

// Callable error codes acceptInvite raises for ordinary user mistakes rather
// than bugs: bad/expired verification code, consumed or expired invite link,
// and the rate limiter.
const EXPECTED_ACCEPT_CODES = new Set([
  'functions/not-found',
  'functions/deadline-exceeded',
  'functions/permission-denied',
  'functions/resource-exhausted',
  'functions/invalid-argument',
  'functions/failed-precondition',
])

class InviteService {
  constructor() {
    this.createInviteFn = httpsCallable(functions, 'createInvite')
    this.getInviteFn = httpsCallable(functions, 'getInvite')
    this.sendInviteCodeFn = httpsCallable(functions, 'sendInviteCode')
    this.acceptInviteFn = httpsCallable(functions, 'acceptInvite')
    this.revokeInviteFn = httpsCallable(functions, 'revokeInvite')
    this.resendInviteFn = httpsCallable(functions, 'resendInvite')
  }

  /**
   * Send an invite to a user
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.companyId
   * @param {string} [params.role='user']
   * @param {string} [params.permissionLevel]
   * @param {string} [params.firstName]
   * @param {string} [params.lastName]
   * @param {boolean} [params.makePrimaryContact] - Set this user as the company's primary contact on accept
   * @returns {Promise<{success: boolean, inviteId: string}>}
   */
  async sendInvite({ email, companyId, role, permissionLevel, firstName, lastName, makePrimaryContact }) {
    try {
      logger.info('[InviteService] Sending invite', { email, companyId })
      const result = await this.createInviteFn({ email, companyId, role, permissionLevel, firstName, lastName, makePrimaryContact })
      logger.info('[InviteService] Invite sent', { inviteId: result.data.inviteId })
      return result.data
    } catch (error) {
      // already-exists = expected validation (user is already a member), shown to the admin
      logger[error?.code === 'functions/already-exists' ? 'warn' : 'error']('[InviteService] Failed to send invite:', error)
      throw this._handleError(error)
    }
  }

  /**
   * Get invite details by token (public, no auth required)
   * @param {string} token
   * @returns {Promise<{email, companyName, inviterName, expiresAt}>}
   */
  async getInvite(token) {
    try {
      const result = await this.getInviteFn({ token })
      return result.data
    } catch (error) {
      // Expected user-facing state when an invite has been consumed, expired
      // (deadline-exceeded is the server's "expired" code), or never existed
      const isMissing = ['functions/not-found', 'functions/deadline-exceeded'].includes(error?.code)
      if (isMissing) {
        logger.warn('[InviteService] Failed to get invite:', error)
      } else {
        logger.error('[InviteService] Failed to get invite:', error)
      }
      throw this._handleError(error)
    }
  }

  /**
   * Send a verification code for invite acceptance
   * @param {string} email
   * @param {string} token - Invite token
   * @returns {Promise<{success: boolean}>}
   */
  async sendInviteCode(email, token) {
    try {
      logger.info('[InviteService] Sending invite code', { email })
      const result = await this.sendInviteCodeFn({ email, token })
      return result.data
    } catch (error) {
      logger.error('[InviteService] Failed to send invite code:', error)
      throw this._handleError(error)
    }
  }

  /**
   * Accept an invite
   * @param {Object} params
   * @param {string} params.token - Invite token
   * @param {string} params.authMethod - 'email_code' or 'oauth'
   * @param {string} [params.email] - Required for email_code
   * @param {string} [params.code] - Required for email_code
   * @returns {Promise<{success: boolean, token: string, isNewUser: boolean}>}
   */
  async acceptInvite({ token, authMethod, email, code, firstName, lastName }) {
    try {
      logger.info('[InviteService] Accepting invite', { authMethod })
      const result = await this.acceptInviteFn({ token, authMethod, email, code, firstName, lastName })
      logger.info('[InviteService] Invite accepted', { isNewUser: result.data.isNewUser })
      return result.data
    } catch (error) {
      // Expected user-facing states (wrong/expired code, dead link, rate limit)
      // already show the user a clear message — logging them at error level just
      // fills the error log with normal invite-flow friction.
      const isExpected = EXPECTED_ACCEPT_CODES.has(error?.code)
      logger[isExpected ? 'warn' : 'error']('[InviteService] Failed to accept invite:', error)
      throw this._handleError(error)
    }
  }

  /**
   * Revoke a pending invite
   * @param {string} inviteId
   * @returns {Promise<{success: boolean}>}
   */
  async revokeInvite(inviteId) {
    try {
      logger.info('[InviteService] Revoking invite', { inviteId })
      const result = await this.revokeInviteFn({ inviteId })
      return result.data
    } catch (error) {
      logger.error('[InviteService] Failed to revoke invite:', error)
      throw this._handleError(error)
    }
  }

  /**
   * Resend a pending invite with new token
   * @param {string} inviteId
   * @returns {Promise<{success: boolean}>}
   */
  async resendInvite(inviteId) {
    try {
      logger.info('[InviteService] Resending invite', { inviteId })
      const result = await this.resendInviteFn({ inviteId })
      return result.data
    } catch (error) {
      logger.error('[InviteService] Failed to resend invite:', error)
      throw this._handleError(error)
    }
  }

  /**
   * Get pending invites for a company (direct Firestore query)
   * @param {string} companyId
   * @returns {Promise<Array>}
   */
  async getCompanyInvites(companyId) {
    try {
      const q = query(
        collection(db, 'invites'),
        where('companyId', '==', companyId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      logger.error('[InviteService] Failed to get company invites:', error)
      return []
    }
  }

  /**
   * Get pending admin/super invites (for AdminManagement)
   * @returns {Promise<Array>}
   */
  async getAdminInvites() {
    try {
      const q = query(
        collection(db, 'invites'),
        where('status', '==', 'pending'),
        where('role', 'in', ['admin', 'super']),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      logger.error('[InviteService] Failed to get admin invites:', error)
      return []
    }
  }

  _handleError(error) {
    const code = error.code || error.details?.code
    const message = error.message || 'An unexpected error occurred.'

    if (code === 'functions/already-exists') {
      return new Error('This user already belongs to this company.')
    }
    if (code === 'functions/not-found') {
      return new Error('This invite link is invalid or has already been used.')
    }
    if (code === 'functions/deadline-exceeded') {
      return new Error('This invite has expired. Please ask your administrator to send a new one.')
    }
    if (code === 'functions/resource-exhausted') {
      return new Error('Too many attempts. Please wait a few minutes.')
    }
    if (code === 'functions/permission-denied') {
      return new Error(message)
    }

    return new Error(message)
  }
}

export const inviteService = new InviteService()
